import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  ALT_DESCRIPTORS_PENDING,
  ALT_RECOMMENDATION_DEFERRED,
  altDomainStatuses,
  altEvidence,
  altLevelIndex,
  altNextPhrase,
  altNowPhrase,
  altValueShort,
  altWhatsHappening,
  type AltDomainStatus,
} from './altPlanContent';
import { altLevelLabel, DOMAIN_LABELS, type PlanStudent } from './derive';
import { CARD_SHADOW, COLORS, NEXT_BOX, TREND_STYLES } from './tokens';

const INK3 = '#7F7F99';

function PhraseBox({ status, next }: { status: AltDomainStatus; next?: boolean }) {
  const level = status.level as number;
  const shown = next ? Math.min(5, altLevelIndex(level) + 1) : altLevelIndex(level);
  const phrase = next ? altNextPhrase(status.domain, level) : altNowPhrase(status.domain, level);
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        bgcolor: next ? NEXT_BOX.bg : COLORS.bg,
        borderRadius: '8px',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
      }}
    >
      <Typography sx={{ fontSize: 16, fontWeight: 700, color: next ? NEXT_BOX.label : COLORS.muted }}>
        {`${next ? 'Next' : 'Now'} • Level ${shown} ${altLevelLabel(shown)}`}
      </Typography>
      {phrase != null && (
        <Typography sx={{ fontSize: 16, lineHeight: '24px', fontWeight: 500, color: COLORS.ink }}>
          {phrase}
        </Typography>
      )}
    </Box>
  );
}

function DomainCard({ status, defaultOpen }: { status: AltDomainStatus; defaultOpen: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const contentId = `alt-fbd-${status.domain}`;
  return (
    <Box sx={{ borderRadius: '8px', outline: `1px solid ${COLORS.divider}`, outlineOffset: '-1px', p: 2 }}>
      <Box
        data-testid={`alt-fbd-head-${status.domain}`}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: COLORS.ink }}>
              {DOMAIN_LABELS[status.domain]}
            </Typography>
            <Typography sx={{ fontSize: 16, fontWeight: 500, color: COLORS.muted }}>
              {altValueShort(status.level, status.scale)}
            </Typography>
          </Box>
          {status.delta != null && status.delta > 0 && (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '2px 10px',
                borderRadius: '9999px',
                bgcolor: TREND_STYLES.rising.bg,
                color: COLORS.ink,
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              <Box component="span" sx={{ fontSize: 10, color: TREND_STYLES.rising.fg }}>
                ▲
              </Box>
              Growing
            </Box>
          )}
        </Box>
        <Box
          role="button"
          tabIndex={0}
          aria-label={`${open ? 'Hide' : 'Show'} ${DOMAIN_LABELS[status.domain]} details`}
          aria-expanded={open}
          aria-controls={contentId}
          onClick={() => setOpen((v) => !v)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setOpen((v) => !v);
            }
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: 16,
            fontWeight: 600,
            letterSpacing: '0.5px',
            color: COLORS.navy,
            cursor: 'pointer',
          }}
        >
          {open ? 'Hide Details' : 'Show Details'}
          {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
        </Box>
      </Box>
      {open && (
        <Box id={contentId} sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1.5 }}>
          {status.level != null && (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <PhraseBox status={status} />
              {altLevelIndex(status.level) < 5 ? (
                <PhraseBox status={status} next />
              ) : (
                <Box sx={{ flex: 1, minWidth: 0, bgcolor: NEXT_BOX.bg, borderRadius: '8px', p: 2 }}>
                  <Typography sx={{ fontSize: 16, lineHeight: '24px', fontWeight: 500, color: NEXT_BOX.label }}>
                    {`${DOMAIN_LABELS[status.domain]} is at the top of the Alternate ACCESS scale (Level 5 — Bridging).`}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', maxWidth: 760 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: COLORS.muted }}>
              What&apos;s Happening
            </Typography>
            <Typography sx={{ fontSize: 16, lineHeight: '24px', fontWeight: 500, color: COLORS.ink }}>
              {altWhatsHappening(status)}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 14, lineHeight: '20px', fontWeight: 500, color: INK3 }}>
            {ALT_RECOMMENDATION_DEFERRED}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', maxWidth: 760 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: COLORS.muted }}>Evidence</Typography>
            <Typography sx={{ fontSize: 16, lineHeight: '24px', fontWeight: 500, color: COLORS.ink }}>
              {altEvidence(status)}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export function AltFocusByDomain({ student }: { student: PlanStudent }) {
  const statuses = altDomainStatuses(student);
  const first = student.firstName || 'This student';
  return (
    <Box sx={{ bgcolor: '#FFFFFF', borderRadius: '12px', boxShadow: CARD_SHADOW, p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Typography sx={{ fontSize: 24, lineHeight: '32px', fontWeight: 600, color: COLORS.ink }}>
          Focus by Domain
        </Typography>
        <Typography sx={{ fontSize: 16, fontWeight: 500, color: COLORS.muted }}>
          {`${first}'s current Alternate level, the next level where one exists, and what's happening in each domain. Instructional recommendations and supports are deferred for Alternate ACCESS — these are IEP / case-conference team decisions.`}
        </Typography>
        <Typography sx={{ fontSize: 14, lineHeight: '20px', fontWeight: 500, color: INK3 }}>
          {ALT_DESCRIPTORS_PENDING}
        </Typography>
      </Box>
      {statuses.map((status, i) => (
        <DomainCard key={`${student.id}-${status.domain}`} status={status} defaultOpen={i === 0} />
      ))}
    </Box>
  );
}
